import {
  AbstractControl,
  FormArray,
  FormControl,
  FormGroup,
  ValidationErrors,
} from '@angular/forms';
import { Injectable } from '@angular/core';

/**
 * References:
 * https://dev.to/klementomeri/how-to-handle-errors-in-angular-reactive-forms-with-3-lines-of-code-3nan
 * https://gist.github.com/klement97/58ab606031c4fe4a2a04b6ed8c2e9d92
 */

export declare interface OutputObject {
  [key: string]: [];
}

@Injectable({
  providedIn: 'root',
})
export class FormValidator {
  private form!: FormGroup;
  private serverError: any;
  private outputObject!: OutputObject;
  private message!: Map<string, string>;
  private objectMessages!: Map<string, Map<string, string>>;

  public fieldHasError(name: string): boolean {
    if (!this.form.get(name)) return false;
    const fieldHasError: boolean =
      FormValidator.hasError(this.form.get(name)!) &&
      this.message.get(name)?.length !== 0;
    return fieldHasError;
  }

  private static hasError(control: AbstractControl): boolean {
    return control.invalid && (control.dirty || control.touched);
  }

  /**
   * Takes server error obj and set errors to appropriate fields at form given.
   *
   * @param serverError       Error object that is received from the server
   * @param form              Form to which errors belong to.
   *
   */
  public organizeServerErrors(serverError: OutputObject, form: FormGroup) {
    if (!(serverError && typeof serverError === 'object')) {
      return;
    }
    this.form = form;
    this.serverError = serverError;
    this.setErrorToFormFields();
    this.findErrors(form);
  }

  /**
   * Listen's for invalid status of the form given and find's it's errors.
   *
   * @param form              Form to be listened
   * @param outputObject       Error object which to set errors.
   * @param objectMessages     Errors message to display in front.
   */
  public setFormValidator(
    form: FormGroup,
    outputObject: any,
    objectMessages?: any,
  ) {
    this.form = form;
    this.message = new Map();
    this.outputObject = outputObject;
    this.setObjectMessages(objectMessages);
    setInterval(() => {
      this.findErrors(this.form);
    }, 400);
  }

  /** Used to call when an invalid form has been submit
   *  By setting error we emit a status changed event for the form.
   */
  public invalidFormHasSubmit() {
    // fixme: changed the listener to value changes so setErrors() wouldn't trigger it
    // fixme: look up for another method to trigger value changes
    this.form?.markAllAsTouched();
    this.form?.setErrors({});
  }

  /** Finds appropriate fields on form and set's the server error. */
  private setErrorToFormFields() {
    Object.keys(this.serverError).forEach(field => {
      if (Array.isArray(this.serverError[field])) {
        const objectMessages: any[] = this.serverError[field];
        this.form
          .get(field)
          ?.setErrors({ serverError: objectMessages[0].message });
      } else {
        this.setErrorsToNestedFields(field);
      }
    });
  }

  /**
   * Takes nested field name and set's server error to appropriate nested field.
   * @param nestedFieldName       Usually formControlName of a FormArray.
   */
  private setErrorsToNestedFields(nestedFieldName: string) {
    const nestedFormErrors = this.serverError[nestedFieldName];
    Object.keys(nestedFormErrors).forEach(field => {
      const formControl = this.form.get(field);
      if (formControl) {
        formControl.setErrors({ serverError: nestedFormErrors[field] });
        return;
      }
      const nestedForm = this.form.get(nestedFieldName);
      const nestedField = nestedForm?.get(field);
      if (nestedField) {
        nestedField.setErrors({ serverError: nestedFormErrors[field] });
      }
    });
  }

  /**
   * Find which control contains the error and set required { control -> error message } combination
   * into the outputObject given previously.
   *
   * @param controls      Abstract Controls of the form which contains errors.
   */
  private findErrors(form: FormGroup) {
    const controls: { [key: string]: AbstractControl } = form.controls;
    Object.keys(controls).forEach((control: string) => {
      if (controls[control] instanceof FormArray) {
        Object.defineProperty(this.outputObject, control, {
          value: [],
          writable: true,
        });
        this.findErrorsOnFormArrays(controls[control] as FormArray, control);
      } else if (controls[control] instanceof FormControl) {
        this.findErrorsOnFormControls(controls, control);
      }
    });

    const groupsErrors = form.errors;
    if(groupsErrors) {
      Object.entries(groupsErrors).forEach(([key, value]) => {
        if (
          this.objectMessages &&
          this.objectMessages.size > 0 ) {
          let fieldResult: string | undefined;
          let messageResult: string | undefined;
          for (const [field, errors] of this.objectMessages.entries()) {
            if (errors.has(key)) {
              fieldResult = field;
              messageResult = errors.get(key);
              break;
            }
          }
          if(fieldResult && messageResult && !this.message.get(fieldResult)) {
            const control = form.get(fieldResult);
            if(control) control.setErrors({ key: true });
            this.message.set(fieldResult, messageResult);
            this.setErrorTooutputObject(fieldResult);
          }
        }
      });
    }
  }

  private findErrorsOnFormArrays(formArray: FormArray, formArrayName: string) {
    let i = 0;
    for (const formGroup of formArray.controls as FormGroup[]) {
      const controls = formGroup.controls;
      const formArrayErrors: any[] = this.outputObject[formArrayName];
      formArrayErrors.push({});
      Object.keys(controls).forEach(control => {
        if (FormValidator.hasError(controls[control])) {
          this.setErrorMessage(control, controls[control].errors || {});
          Object.defineProperty(formArrayErrors[i], control, {
            value: this.message,
            writable: true,
          });
        } else this.message.set(control, '');
      });
      i++;
    }
  }

  private findErrorsOnFormControls(
    controls: { [key: string]: AbstractControl },
    control: string,
  ) {
    if (FormValidator.hasError(controls[control])) {
      this.setErrorMessage(control, controls[control].errors || {});
    } else this.message.set(control, '');
    this.setErrorTooutputObject(control);
  }

  /**
   * Find's error type and set's a message value for this type.
   *
   * @param errors        Validation Error obj.
   */
  private setErrorMessage(field: string, errors: ValidationErrors) {
    if (this.isErrorMessageWithMap(field, errors)) {
    } else if (errors['serverError']) {
      this.message.set(field, errors['serverError']);
    } else if (errors['required']) {
      this.message.set(field, `Este campo es requerido`);
    } else if (errors['minlength']) {
      this.message.set(
        field,
        this.capitalize(field) +
          ` debe tener ${errors['minlength'].requiredLength} caracteres`,
      );
    } else if (errors['maxlength']) {
      this.message.set(
        field,
        this.capitalize(field) +
          ` debe tener ${errors['maxlength'].requiredLength} caracteres`,
      );
    } else if (errors['email']) {
      this.message.set(field, 'Email invalido');
    } else if (errors['min']) {
      this.message.set(
        field,
        `El valor mínimo es ${errors['min'].min}, el actual es ${errors['min'].actual}`,
      );
    } else if (errors['max']) {
      this.message.set(
        field,
        `El valor máximo es ${errors['max'].max}, el actual es ${errors['max'].actual}`,
      );
    } else if (errors['pattern']) {
      this.message.set(field, 'Valor invalido');
    } else if (errors['passwordMismatch']) {
      this.message.set(field, 'Las contraseñas no coinciden');
    } else {
      this.message.set(field, '');
    }
  }

  /**
   * Set's a new property to outputObject with key from the field's name and error message as a value.
   * @param field         Field which contains error.
   */
  private setErrorTooutputObject(field: string) {
    const value = this.message ? this.message.get(field) : '';
    Object.defineProperty(this.outputObject, field, {
      value,
      writable: true,
    });
  }

  private setObjectMessages(objectMessages?: any) {
    if (typeof objectMessages !== 'undefined') {
      this.objectMessages = new Map();
      Object.keys(objectMessages).map(key => {
        this.objectMessages.set(
          key,
          new Map(Object.entries(objectMessages[key])),
        );
      });
    }
  }

  private isErrorMessageWithMap(
    field: string,
    errors: ValidationErrors,
  ): boolean {
    let flag: boolean = false;
    if (
      this.objectMessages &&
      this.objectMessages.size > 0 &&
      this.objectMessages.get(field)
    ) {
      this.objectMessages.get(field)?.forEach((value, key) => {
        if (errors[key]) {
          this.message.set(field, value);
          flag = true;
        }
      });
    } else {
      return false;
    }
    return flag;
  }

  private capitalize(text: string) {
    return text.charAt(0).toUpperCase() + text.slice(1);
  }
}
